import React, { useCallback } from "react";
import barIcon from "./barIcon.svg";
import pieIcon from "./pieIcon.svg";
import lineIcon from "./lineIcon.svg";
import numberIcon from "./numberIcon.svg";
import calendarIcon from "./calendarIcon.svg";
import stackedBarIcon from "./stackedBarIcon.svg";
import { Widget } from "/app/src/models";
import Title from "./title";
import Datasets from "./datasets";
import { Button, Select, SelectItem, Tooltip } from "@heroui/react";
import PageHeader from "../../../generic/components/pageHeader";
import TrendLines from "./trendlines";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { widgetService } from "/app/src/services";
import { handlePromiseError } from "/app/src/helpers/api";

/**
 * EditWidget renders the widget editing interface and handles updates and deletion.
 * @param widget - the widget data
 * @returns JSX element representing the edit widget interface
 */
export default function EditWidget({ widget }: { widget: Widget }) {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] =
    React.useState<string>("datasets");
  const queryClient = useQueryClient();
  const { mutateAsync: updateWidget } = useMutation({
    mutationFn: (values: Omit<Widget, "datasets">) => {
      return widgetService
        .updateSingle(values.id, values)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["widgets", widget.viewId],
        (oldData: { widgets: Widget[] }) => {
          return {
            widgets: oldData.widgets.map((widget) => {
              if (widget.id === response.widget.id) {
                return response.widget;
              }
              return widget;
            }),
          };
        },
      );
    },
  });

  const { mutateAsync: deleteWidget } = useMutation({
    mutationFn: (id: number) => {
      return widgetService.deleteSingle(id).then(handlePromiseError);
    },
    onSuccess: () => {
      queryClient.setQueryData(
        ["widgets", widget.viewId],
        (oldData: { widgets: Widget[] }) => {
          return {
            widgets: oldData.widgets.filter((w) => {
              return w.id !== widget.id;
            }),
          };
        },
      );
    },
  });

  /**
   * Handles the deletion of the widget.
   */
  const handleDeleteClick = useCallback((): void => {
    deleteWidget(widget.id);
  }, [deleteWidget, widget.id]);

  /**
   * Returns a function that updates the widget's chart type and refreshes the data.
   * @param type - the chart type to switch to
   * @returns A callback function to handle chart type change
   */
  const handleChartTypeChange = useCallback(
    (type) => {
      return () => {
        updateWidget({ id: widget.id, chartType: type });
        //on chart change we need to refresh the data
        queryClient.invalidateQueries(["widgetData", widget.id]);
      };
    },
    [queryClient, updateWidget, widget.id],
  );

  /**
   * Updates the selected option: Either "datasets" or "trendlines"
   * @param value the value of the selected option
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedOption(event.target.value);
    },
    [],
  );
  return (
    <div className="editWidget">
      <PageHeader
        title={<Title widget={widget} updateWidget={updateWidget} />}
        deleteFn={handleDeleteClick}
        modalHeader={t("translation:delete_widget")}
      />
      <div className="mt-3">
        <Tooltip size="sm" content={t("translation:bar_chart")}>
          <span>
            <Button
              variant={widget.chartType === "bar" ? "flat" : "bordered"}
              radius="none"
              color={widget.chartType === "bar" ? "primary" : "default"}
              size="sm"
              onPress={handleChartTypeChange("bar")}
              className="mr-1"
            >
              <img className="icon" src={barIcon} alt="bar" />
            </Button>
          </span>
        </Tooltip>
        <Tooltip size="sm" content={t("translation:number")}>
          <span>
            <Button
              variant={widget.chartType === "number" ? "flat" : "bordered"}
              radius="none"
              color={widget.chartType === "number" ? "primary" : "default"}
              size="sm"
              onPress={handleChartTypeChange("number")}
              className="mr-1"
            >
              <img className="icon" src={numberIcon} alt="number" />
            </Button>
          </span>
        </Tooltip>

        <Tooltip size="sm" content={t("translation:line_chart")}>
          <span>
            <Button
              variant={widget.chartType === "line" ? "flat" : "bordered"}
              radius="none"
              color={widget.chartType === "line" ? "primary" : "default"}
              size="sm"
              onPress={handleChartTypeChange("line")}
              className="mr-1"
            >
              <img className="icon" src={lineIcon} alt="line" />
            </Button>
          </span>
        </Tooltip>
        <Tooltip size="sm" content={t("translation:pie_chart")}>
          <span>
            <Button
              variant={widget.chartType === "pie" ? "flat" : "bordered"}
              radius="none"
              color={widget.chartType === "pie" ? "primary" : "default"}
              size="sm"
              onPress={handleChartTypeChange("pie")}
              className="mr-1"
            >
              <img className="icon" src={pieIcon} alt="pie" />
            </Button>
          </span>
        </Tooltip>
        <Tooltip size="sm" content={t("translation:stacked_bar_chart")}>
          <span>
            <Button
              variant={widget.chartType === "stackedBar" ? "flat" : "bordered"}
              radius="none"
              color={widget.chartType === "stackedBar" ? "primary" : "default"}
              size="sm"
              onPress={handleChartTypeChange("stackedBar")}
              className="mr-1"
            >
              <img className="icon" src={stackedBarIcon} alt="stackedBar" />
            </Button>
          </span>
        </Tooltip>
        <Tooltip size="sm" content={t("translation:calendar_chart")}>
          <span>
            <Button
              variant={widget.chartType === "calendar" ? "flat" : "bordered"}
              radius="none"
              color={widget.chartType === "calendar" ? "primary" : "default"}
              size="sm"
              onPress={handleChartTypeChange("calendar")}
            >
              <img className="icon" src={calendarIcon} alt="calendar" />
            </Button>
          </span>
        </Tooltip>
      </div>

      <div className="mt-3">
        <Select
          aria-label="Data display type"
          size="md"
          fullWidth={false}
          disallowEmptySelection
          onChange={handleChange}
          defaultSelectedKeys={["datasets"]}
        >
          <SelectItem key="datasets">{t("translation:snapshots")}</SelectItem>
          <SelectItem key="trendlines">
            {t("translation:target_lines")}
          </SelectItem>
        </Select>{" "}
      </div>
      {selectedOption === "datasets" && <Datasets widget={widget} />}
      {selectedOption === "trendlines" && <TrendLines widget={widget} />}
    </div>
  );
}
